/**
 * Banner Component
 * Displays a banner image with optional admin edit button
 */
import { useState, useRef, useEffect } from 'react';
import { apiClient, Banner as BannerType } from '../lib/api-client';
import { resolveMediaUrl } from '../lib/media-url';

// Simple inline SVG icons
const PencilIcon = ({ className }: { className?: string }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
    </svg>
);

const XMarkIcon = ({ className }: { className?: string }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
);

const PhotoIcon = ({ className }: { className?: string }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
);

interface BannerProps {
    location: string;
    className?: string;
    imgClassName?: string;
    showEditButton?: boolean;
    onImageChange?: (newImagePath: string) => void;
}

export function Banner({
    location,
    className = '',
    imgClassName = '',
    showEditButton = false,
    onImageChange,
}: BannerProps) {
    const [banner, setBanner] = useState<BannerType | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        loadBanner();
    }, [location]);

    const loadBanner = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await apiClient.getBanner(location);
            setBanner(data);
        } catch (err) {
            console.error('Failed to load banner:', err);
            setError('バナーの読み込みに失敗しました');
        } finally {
            setLoading(false);
        }
    };

    const handleEditClick = () => {
        setShowModal(true);
    };

    const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Validate file type
        const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            setError('JPEG, PNG, GIF, WebP形式の画像のみアップロードできます');
            return;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            setError('ファイルサイズは5MB以下にしてください');
            return;
        }

        try {
            setUploading(true);
            setError(null);
            const result = await apiClient.uploadBannerImage(location, file);

            // Update banner with new image path
            if (banner) {
                setBanner({ ...banner, image_path: result.image_path });
            }

            onImageChange?.(result.image_path);
            setShowModal(false);
        } catch (err) {
            console.error('Failed to upload banner:', err);
            setError('画像のアップロードに失敗しました');
        } finally {
            setUploading(false);
        }
    };

    const triggerFileSelect = () => {
        fileInputRef.current?.click();
    };

    if (loading) {
        return (
            <div className={`animate-pulse bg-gray-700 ${className}`}>
                <div className={`w-full h-20 ${imgClassName}`} />
            </div>
        );
    }

    // Determine the image to display (show if image exists)
    const imageToShow = banner?.image_path ? resolveMediaUrl(banner.image_path) : null;
    const altText = banner?.alt_text || '';
    const linkUrl = banner?.link_url;

    // If no image available, return null (banner area is hidden)
    if (!imageToShow) {
        return null;
    }

    return (
        <div className={`relative group ${className}`}>
            {linkUrl ? (
                <a href={linkUrl} target="_blank" rel="noopener noreferrer">
                    <img
                        src={imageToShow}
                        alt={altText}
                        className={imgClassName}
                    />
                </a>
            ) : (
                <img
                    src={imageToShow}
                    alt={altText}
                    className={imgClassName}
                />
            )}

            {/* Admin edit button */}
            {showEditButton && (
                <button
                    onClick={handleEditClick}
                    className="absolute top-2 right-2 p-1.5 bg-black/60 hover:bg-black/80 rounded-full text-white opacity-0 group-hover:opacity-100 transition-opacity"
                    title="バナーを編集"
                >
                    <PencilIcon className="w-4 h-4" />
                </button>
            )}

            {/* Edit modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
                    <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-semibold text-white">
                                バナー画像を変更
                            </h3>
                            <button
                                onClick={() => setShowModal(false)}
                                className="text-gray-400 hover:text-white"
                            >
                                <XMarkIcon className="w-6 h-6" />
                            </button>
                        </div>

                        {/* Current image preview */}
                        <div className="mb-4">
                            <p className="text-sm text-gray-400 mb-2">現在の画像:</p>
                            <img
                                src={imageToShow}
                                alt={altText}
                                className="w-full rounded"
                            />
                        </div>

                        {/* File upload area */}
                        <div
                            onClick={triggerFileSelect}
                            className="border-2 border-dashed border-gray-600 rounded-lg p-6 text-center cursor-pointer hover:border-pink-500 transition-colors"
                        >
                            <PhotoIcon className="w-12 h-12 mx-auto text-gray-500 mb-2" />
                            <p className="text-gray-400">
                                {uploading ? 'アップロード中...' : 'クリックして新しい画像を選択'}
                            </p>
                            <p className="text-sm text-gray-500 mt-1">
                                JPEG, PNG, GIF, WebP (最大5MB)
                            </p>
                        </div>

                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/jpeg,image/png,image/gif,image/webp"
                            onChange={handleFileSelect}
                            className="hidden"
                        />

                        {error && (
                            <p className="mt-4 text-red-400 text-sm">{error}</p>
                        )}

                        <div className="mt-4 flex justify-end">
                            <button
                                onClick={() => setShowModal(false)}
                                className="px-4 py-2 text-gray-400 hover:text-white"
                            >
                                キャンセル
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Banner;
